class ProductBundlePack extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-pack-config]').textContent);
    this.mode = this.config.mode || 'single-product';
    this.addBtn = this.querySelector('[data-pack-add]');
    this.itemCards = [...this.querySelectorAll('[data-bundle-item]')];
    this.separateTotalEl = this.querySelector('[data-pack-separate-total]');
    this.savingsEl = this.querySelector('[data-pack-savings]');
    this.minimal = !!this.querySelector('.b3-item-card--minimal');

    if (this.mode === 'multi-product') {
      this.initMultiProduct();
    } else {
      this.initSingleProduct();
    }

    this.addBtn.addEventListener('click', () => this.addToCart());
    this.ensureSizeOptionsRendered();
    this.syncAllSlotsFromDom();

    // Start every slot on its first in-stock size and grey out sold-out sizes.
    if (this.mode === 'multi-product') {
      this.itemCards.forEach((_, index) => {
        const state = this.slotState[index];
        if (state && state.productId) this.onProductChange(index, state.productId);
      });
    }

    this.refresh();
  }

  norm(value) {
    return String(value ?? '').trim();
  }

  getProduct(productId) {
    const id = Number(productId);
    return this.eligibleProducts.find((p) => Number(p.id) === id);
  }

  // In the minimal bundles, each product IS one color: the Color option is fixed
  // to the product's own color (taken from its first variant) and only the
  // remaining options (Size) are shown to the customer.
  isColorOption(opt) {
    return /^colou?rs?$/i.test(this.norm(opt.name));
  }

  pinnedValue(product, opt) {
    if (!this.minimal || !this.isColorOption(opt)) return null;
    const own = product.variants?.[0]?.options?.[opt.position - 1];
    return this.norm(own) ? own : null;
  }

  visibleOptions(product) {
    return product.options.filter((opt) => this.pinnedValue(product, opt) === null);
  }

  initMultiProduct() {
    this.eligibleProducts = this.config.eligibleProducts || [];
    this.slotState = this.itemCards.map(() => ({
      productId: null,
      selections: {},
    }));

    this.addEventListener('change', (event) => {
      const productInput = event.target.closest('[data-pack-product-option]');
      if (productInput) {
        const itemIndex = parseInt(productInput.dataset.itemIndex, 10);
        const productId = parseInt(productInput.value, 10);
        this.onProductChange(itemIndex, productId);
        return;
      }

      const sizeInput = event.target.closest('[data-pack-size-option]');
      if (sizeInput) {
        const itemIndex = parseInt(sizeInput.dataset.itemIndex, 10);
        const position = parseInt(sizeInput.dataset.optionPosition, 10);
        this.slotState[itemIndex].selections[position] = sizeInput.value;
        this.refresh();
      }
    });

    this.syncAllSlotsFromDom();
  }

  initSingleProduct() {
    this.items = this.config.items || [];
    this.selections = this.items.map((item) => {
      const selected = {};
      item.options.forEach((opt) => {
        selected[opt.position] = opt.selected;
      });
      return selected;
    });

    this.querySelectorAll('[data-bundle-option]').forEach((input) => {
      input.addEventListener('change', (event) => {
        const itemIndex = parseInt(event.target.dataset.itemIndex, 10);
        const position = parseInt(event.target.dataset.optionPosition, 10);
        this.selections[itemIndex][position] = event.target.value;
        this.refresh();
      });
    });
  }

  syncAllSlotsFromDom() {
    if (this.mode !== 'multi-product') return;
    this.itemCards.forEach((_, index) => this.syncSlotFromDom(index));
  }

  syncSlotFromDom(itemIndex) {
    const card = this.itemCards[itemIndex];
    if (!card || !this.slotState[itemIndex]) return;

    const productInput = card.querySelector('[data-pack-product-option]:checked');
    if (productInput) {
      this.slotState[itemIndex].productId = parseInt(productInput.value, 10);
    }

    const product = this.getProduct(this.slotState[itemIndex].productId);
    if (!product) return;

    product.options.forEach((opt) => {
      const pinned = this.pinnedValue(product, opt);
      if (pinned !== null) {
        this.slotState[itemIndex].selections[opt.position] = pinned;
        return;
      }

      const sizeInput = card.querySelector(
        `[data-pack-size-option][data-option-position="${opt.position}"]:checked`
      );
      if (sizeInput) {
        this.slotState[itemIndex].selections[opt.position] = sizeInput.value;
      } else if (!this.slotState[itemIndex].selections[opt.position]) {
        this.slotState[itemIndex].selections[opt.position] = opt.selected || opt.values?.[0] || null;
      }
    });
  }

  updateCardDisplay(itemIndex, product, variant) {
    const card = this.itemCards[itemIndex];
    if (!card || !product) return;

    const titleEl = card.querySelector('[data-pack-product-title]');
    if (titleEl) titleEl.textContent = product.title;

    const imageSrc = variant?.featured_image || product.image;
    if (!imageSrc) return;

    const imgWrap = card.querySelector('.b3-item-img');
    let imageEl = card.querySelector('[data-bundle-item-image]');

    if (!imageEl && imgWrap) {
      imgWrap.innerHTML = '';
      imageEl = document.createElement('img');
      imageEl.setAttribute('data-bundle-item-image', '');
      imageEl.width = 80;
      imageEl.height = 80;
      imageEl.loading = 'lazy';
      imageEl.alt = product.title;
      imgWrap.appendChild(imageEl);
    }

    if (imageEl) {
      imageEl.src = imageSrc;
      imageEl.alt = product.title;
    }
  }

  onProductChange(itemIndex, productId) {
    const product = this.getProduct(productId);
    if (!product) return;

    this.slotState[itemIndex].productId = productId;
    this.slotState[itemIndex].selections = {};

    // Fixed options first (the product's own color), so size availability
    // below is checked against the right color.
    product.options.forEach((opt) => {
      const pinned = this.pinnedValue(product, opt);
      if (pinned !== null) this.slotState[itemIndex].selections[opt.position] = pinned;
    });

    this.visibleOptions(product).forEach((opt) => {
      const fallback = opt.values?.[0] || opt.selected;
      this.slotState[itemIndex].selections[opt.position] = this.getFirstAvailableValue(
        itemIndex,
        productId,
        opt.position,
        fallback
      );
    });

    const card = this.itemCards[itemIndex];
    const sizeContainer = card.querySelector('[data-pack-size-options]');

    if (sizeContainer) {
      sizeContainer.innerHTML = this.buildSizeHtml(itemIndex, product);
    }

    this.updateCardDisplay(itemIndex, product);
    this.refresh();
  }

  ensureSizeOptionsRendered() {
    if (this.mode !== 'multi-product') return;

    this.itemCards.forEach((card, itemIndex) => {
      const sizeContainer = card.querySelector('[data-pack-size-options]');
      if (!sizeContainer || sizeContainer.querySelector('[data-pack-size-option]')) return;

      const product = this.getProduct(this.slotState[itemIndex]?.productId);
      if (!product) return;

      sizeContainer.innerHTML = this.buildSizeHtml(itemIndex, product);
    });
  }

  buildSizeHtml(itemIndex, product) {
    return this.visibleOptions(product)
      .map((opt) =>
        (opt.values || [])
          .map((value, valueIndex) => {
            const id = `pack-${itemIndex}-size-${opt.position}-${valueIndex}`;
            const selected = this.norm(this.slotState[itemIndex].selections[opt.position]) === this.norm(value);
            const available = this.isValueAvailable(itemIndex, product.id, opt.position, value);
            const checked = selected || (valueIndex === 0 && !this.slotState[itemIndex].selections[opt.position]);

            return `
              <input
                type="radio"
                id="${id}"
                name="pack-${itemIndex}-size"
                value="${value}"
                data-pack-size-option
                data-item-index="${itemIndex}"
                data-option-position="${opt.position}"
                ${checked ? 'checked' : ''}
                ${available ? '' : 'disabled'}
              >
              <label for="${id}" class="${available ? '' : 'is-unavailable'}">${value}</label>
            `;
          })
          .join('')
      )
      .join('');
  }

  getFirstAvailableValue(itemIndex, productId, position, fallback) {
    const product = this.getProduct(productId);
    if (!product) return fallback;

    const opt = product.options.find((o) => o.position === position);
    if (!opt) return fallback;

    const available = (opt.values || []).find((value) =>
      this.isValueAvailable(itemIndex, productId, position, value)
    );
    return available || fallback;
  }

  isValueAvailable(itemIndex, productId, position, value) {
    const product = this.getProduct(productId);
    if (!product) return false;

    const trial = { ...this.slotState[itemIndex].selections, [position]: value };

    return product.variants.some((variant) => {
      if (!variant.available) return false;
      return product.options.every(
        (opt) => this.norm(variant.options[opt.position - 1]) === this.norm(trial[opt.position])
      );
    });
  }

  findMultiProductVariant(itemIndex) {
    this.syncSlotFromDom(itemIndex);

    const state = this.slotState[itemIndex];
    const product = this.getProduct(state.productId);
    if (!product) return null;

    if (!product.options.length) {
      return product.variants.find((v) => v.available) || null;
    }

    return product.variants.find((variant) => {
      if (!variant.available) return false;
      return product.options.every(
        (opt) => this.norm(variant.options[opt.position - 1]) === this.norm(state.selections[opt.position])
      );
    });
  }

  findSingleProductVariant(itemIndex) {
    const item = this.items[itemIndex];
    const selected = this.selections[itemIndex];

    if (!item.options.length) {
      return item.variants.find((v) => v.available) || null;
    }

    return item.variants.find((variant) => {
      if (!variant.available) return false;
      return item.options.every(
        (opt) => this.norm(variant.options[opt.position - 1]) === this.norm(selected[opt.position])
      );
    });
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, this.config.moneyFormat);
    }
    return 'Rs.' + (cents / 100).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  refresh() {
    let allAvailable = true;
    let separateTotal = 0;

    if (this.mode === 'multi-product') {
      this.syncAllSlotsFromDom();

      this.itemCards.forEach((card, itemIndex) => {
        const variant = this.findMultiProductVariant(itemIndex);
        const product = this.getProduct(this.slotState[itemIndex].productId);
        const msgEl = card.querySelector('[data-item-unavailable]');

        if (!variant) {
          allAvailable = false;
          if (msgEl) msgEl.hidden = false;
          return;
        }

        if (msgEl) msgEl.hidden = true;
        separateTotal += variant.price;
        this.updateCardDisplay(itemIndex, product, variant);
      });
    } else {
      this.items.forEach((item, itemIndex) => {
        const variant = this.findSingleProductVariant(itemIndex);
        const card = this.itemCards[itemIndex];
        const msgEl = card?.querySelector('[data-item-unavailable]');

        if (!variant) {
          allAvailable = false;
          if (msgEl) msgEl.hidden = false;
          return;
        }

        if (msgEl) msgEl.hidden = true;
        separateTotal += variant.price;

        const image = card?.querySelector('[data-bundle-item-image]');
        if (image && variant.featured_image) image.src = variant.featured_image;
      });
    }

    if (this.separateTotalEl) this.separateTotalEl.textContent = this.formatMoney(separateTotal);
    if (this.savingsEl) {
      const savings = Math.max(0, separateTotal - this.config.packPrice);
      this.savingsEl.textContent = this.formatMoney(savings);
    }

    this.addBtn.disabled = !allAvailable;
    this.addBtn.classList.toggle('is-ready', allAvailable);
  }

  async addToCart() {
    this.syncAllSlotsFromDom();

    const cartItems = [];
    const label = `${this.config.packQuantity}-pack: ${this.config.packTitle}`;
    const missing = [];

    if (this.mode === 'multi-product') {
      this.slotState.forEach((state, itemIndex) => {
        const variant = this.findMultiProductVariant(itemIndex);
        const product = this.getProduct(state.productId);

        if (!variant) {
          missing.push(itemIndex + 1);
          return;
        }

        cartItems.push({
          id: variant.id,
          quantity: 1,
          properties: {
            Pack: label,
            Item: `Item ${itemIndex + 1}`,
            Color: product?.title || '',
            '_bundle_pack': this.config.bundleId,
          },
        });
      });
    } else {
      this.items.forEach((item, itemIndex) => {
        const variant = this.findSingleProductVariant(itemIndex);
        if (!variant) {
          missing.push(itemIndex + 1);
          return;
        }

        cartItems.push({
          id: variant.id,
          quantity: 1,
          properties: {
            Pack: label,
            Item: `Item ${itemIndex + 1}`,
            '_bundle_pack': this.config.bundleId,
          },
        });
      });
    }

    if (missing.length) {
      window.alert(`Please select a valid size for item(s): ${missing.join(', ')}`);
      this.refresh();
      return;
    }

    if (cartItems.length !== this.config.packQuantity) {
      window.alert('Could not build the pack. Please check all selections.');
      return;
    }

    const defaultLabel = this.addBtn.textContent;
    this.addBtn.textContent = 'Adding…';
    this.addBtn.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems,
          sections: ['cart-drawer', 'cart-icon-bubble'],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const html = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const drawer = document.querySelector('cart-drawer');

        if (drawer) {
          const inner = drawer.querySelector('.drawer__inner');
          const newInner = html.querySelector('.drawer__inner');

          if (inner && newInner) {
            inner.innerHTML = newInner.innerHTML;
            drawer.classList.remove('is-empty');
            drawer.open();
          }
        }

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
      } else {
        window.alert(data.description || data.message || 'Could not add pack to cart.');
      }
    } catch (error) {
      console.error('Pack bundle error:', error);
      window.alert('Something went wrong. Please try again.');
    } finally {
      this.addBtn.textContent = defaultLabel;
      this.refresh();
    }
  }
}

if (!customElements.get('product-bundle-pack')) {
  customElements.define('product-bundle-pack', ProductBundlePack);
}